import { client } from '../apiClient';

const BRAND24_ENDPOINT = '/brand24';

/** Builds a `?from=&to=` query string (YYYY-MM-DD) for the date-filtered section endpoints. */
const rangeQuery = (from?: string, to?: string): string => {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const query = params.toString();
  return query ? `?${query}` : '';
};

export type Brand24Mode = 'OWN_API_KEY' | 'DEFAULT';

export interface Brand24ConnectionStatus {
  isConnected: boolean;
  mode: Brand24Mode | null;
  hasApiKey: boolean;
  socialEnabled: boolean;
}

export interface Brand24Project {
  id: number;
  name: string;
}

export type Brand24ProjectRole = 'MAIN' | 'COMPETITOR';

/** One Brand24 project linked to a ChatBeat project (main brand or a competitor). */
export interface Brand24LinkedProject {
  role: Brand24ProjectRole;
  chatbeatBrand: string;
  chatbeatBrandLabel: string;
  brand24ProjectId: number;
  brand24ProjectName: string;
}

/** One brand row the user configured in the popup. */
export interface Brand24BrandInput {
  role: Brand24ProjectRole;
  chatbeatBrand: string;
  chatbeatBrandLabel: string;
  /** OWN_API_KEY only: an existing Brand24 project the user picked; omit to create a new one. */
  brand24ProjectId?: number;
  brand24ProjectName?: string;
}

export interface Brand24ProjectConfig {
  chatbeatProjectId: number;
  projectLabel: string;
  brands: Brand24LinkedProject[];
}

/** Live Brand24 metrics for one linked project (AI Backlink Competitors table). */
export interface Brand24ProjectMetric {
  brand24ProjectId: number;
  role: Brand24ProjectRole;
  brand: string;
  mentions: number;
  aiBacklinks: number;
  sentiment: number | null;
  tone: 'positive' | 'neutral' | 'negative';
}

/** One word in the main brand's "Context of a discussion" cloud. */
export interface Brand24ContextWord {
  text: string;
  weight: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  /** % of the sampled mentions that contain this word. */
  share: number;
}

/** One row of the main brand's "Discussion Topics" table. */
export interface Brand24DiscussionTopic {
  name: string;
  tone: 'positive' | 'neutral' | 'negative';
  mentions: number;
}

/** "Top AI Source Domains", per AI model: domains it cites that Brand24 also mentions the brand on. */
export interface Brand24AiSourceModel {
  model: string;
  total: number;
  domains: { domain: string; count: number; brandMentions: number }[];
}

/** Headline "Mentions in AI Sources" stat. */
export interface Brand24MentionsScore {
  /** Number of the brand's mentions on AI-cited domains (shown as "score of total"). */
  score: number;
  /** The brand's total mentions in the range (the "of N" denominator; ratio drives the icon). */
  total: number;
  trend: number;
}

/** A social source tile (Facebook / Instagram / X) with its live connection status. */
export interface Brand24SocialSource {
  platform: string;
  name: string;
  domain: string;
  connected: boolean;
}

/** One row of the "AI Backlinks" table. */
export interface Brand24Backlink {
  source: string;
  mentions: number;
  reach: number;
  models: string[];
  tone: 'positive' | 'neutral' | 'negative';
}

export class Brand24Service {
  static async getConnectionStatus(): Promise<Brand24ConnectionStatus> {
    const response = await client(true, true).get(`${BRAND24_ENDPOINT}/connection-status`);
    return response.data;
  }

  static async connect(
    mode: Brand24Mode,
    apiKey?: string,
    brand24AccountId?: number
  ): Promise<boolean> {
    const response = await client(true, true).post(`${BRAND24_ENDPOINT}/connect`, {
      mode,
      apiKey,
      brand24AccountId,
    });
    return response.status === 200;
  }

  /** Projects available to attach (the picker popup): the user's own in OWN_API_KEY mode. */
  static async getProjects(): Promise<Brand24Project[]> {
    const response = await client(true, true).get(`${BRAND24_ENDPOINT}/projects`);
    return response.data?.projects ?? [];
  }

  /** Configures a project's Brand24 projects (main brand + up to 4 competitors) in one batch. */
  static async configureProject(
    chatbeatProjectId: number,
    brands: Brand24BrandInput[]
  ): Promise<Brand24ProjectConfig> {
    const response = await client(true, true).post(`${BRAND24_ENDPOINT}/links`, {
      chatbeatProjectId,
      brands,
    });
    return response.data;
  }

  /** All of the account's projects that have a Brand24 configuration. */
  static async getLinks(): Promise<Brand24ProjectConfig[]> {
    const response = await client(true, true).get(`${BRAND24_ENDPOINT}/links`);
    return response.data?.links ?? [];
  }

  /** The Brand24 configuration for a single project (access-checked server-side), or null if none. */
  static async getProjectConfig(chatbeatProjectId: number): Promise<Brand24ProjectConfig | null> {
    const response = await client(true, true).get(`${BRAND24_ENDPOINT}/links/${chatbeatProjectId}`);
    return response.data?.link ?? null;
  }

  /** Live metrics (AI Backlinks + sentiment) for a project's linked Brand24 projects. */
  static async getProjectMetrics(
    chatbeatProjectId: number,
    from?: string,
    to?: string
  ): Promise<Brand24ProjectMetric[]> {
    const response = await client(true, true).get(
      `${BRAND24_ENDPOINT}/metrics/${chatbeatProjectId}${rangeQuery(from, to)}`
    );
    return response.data?.metrics ?? [];
  }

  /** "Context of a discussion" word cloud for the main brand's Brand24 project. */
  static async getBrandContext(
    chatbeatProjectId: number,
    from?: string,
    to?: string
  ): Promise<Brand24ContextWord[]> {
    const response = await client(true, true).get(
      `${BRAND24_ENDPOINT}/context/${chatbeatProjectId}${rangeQuery(from, to)}`
    );
    return response.data?.words ?? [];
  }

  /** "Discussion Topics" for the main brand's Brand24 project. */
  static async getDiscussionTopics(
    chatbeatProjectId: number,
    from?: string,
    to?: string
  ): Promise<Brand24DiscussionTopic[]> {
    const response = await client(true, true).get(
      `${BRAND24_ENDPOINT}/topics/${chatbeatProjectId}${rangeQuery(from, to)}`
    );
    return response.data?.topics ?? [];
  }

  /**
   * Per-day mentions trend (main brand + competitors) as a chart-ready matrix: first row is
   * `['date', ...brandLabels]`, each following row a date with one count per brand.
   */
  static async getMentionsTrend(
    chatbeatProjectId: number,
    from?: string,
    to?: string
  ): Promise<(string | number)[][]> {
    const response = await client(true, true).get(
      `${BRAND24_ENDPOINT}/trend/${chatbeatProjectId}${rangeQuery(from, to)}`
    );
    return response.data?.trend ?? [];
  }

  /**
   * Consolidated AI-sources data in one call: "Top AI Source Domains" / "Mentions per Model" (sourceDomains)
   * and "AI Backlinks" (backlinks) — computed from a single shared backend pass.
   */
  static async getAiSourceOverview(
    chatbeatProjectId: number,
    from?: string,
    to?: string
  ): Promise<{ sourceDomains: Brand24AiSourceModel[]; backlinks: Brand24Backlink[] }> {
    const response = await client(true, true).get(
      `${BRAND24_ENDPOINT}/ai-sources/${chatbeatProjectId}${rangeQuery(from, to)}`
    );
    return {
      sourceDomains: response.data?.sourceDomains ?? [],
      backlinks: response.data?.backlinks ?? [],
    };
  }

  /** Social source tiles (FB / Instagram / X) connection status — empty unless OWN_API_KEY mode. */
  static async getSocialSources(
    chatbeatProjectId: number,
    from?: string,
    to?: string
  ): Promise<Brand24SocialSource[]> {
    const response = await client(true, true).get(
      `${BRAND24_ENDPOINT}/social/${chatbeatProjectId}${rangeQuery(from, to)}`
    );
    return response.data?.sources ?? [];
  }

  /** Headline "Mentions in AI Sources" stat (AI-source coverage score + mentions trend). */
  static async getMentionsScore(
    chatbeatProjectId: number,
    from?: string,
    to?: string
  ): Promise<Brand24MentionsScore> {
    const response = await client(true, true).get(
      `${BRAND24_ENDPOINT}/mentions-score/${chatbeatProjectId}${rangeQuery(from, to)}`
    );
    return response.data ?? { score: 0, trend: 0 };
  }

  static async unlinkProject(chatbeatProjectId: number): Promise<boolean> {
    const response = await client(true, true).delete(`${BRAND24_ENDPOINT}/links/${chatbeatProjectId}`);
    return response.status === 200;
  }

  static async disconnect(): Promise<boolean> {
    try {
      const response = await client(true, true).post(`${BRAND24_ENDPOINT}/disconnect`);
      return response.status === 200;
    } catch (error) {
      console.error('Error disconnecting from Brand24:', error);
      return false;
    }
  }
}
